import { useMemo } from "react";
import useSWR from "swr";

import { fetchTrades } from "lib/indexer/client";
import { bigNumberify } from "lib/numbers";

export type PeriodAccountStats = {
  closedCount: number;
  cumsumCollateral: bigint;
  cumsumSize: bigint;
  losses: number;
  maxCapital: bigint;
  netCapital: bigint;
  realizedFees: bigint;
  realizedPnl: bigint;
  realizedPriceImpact: bigint;
  startUnrealizedFees: bigint;
  startUnrealizedPnl: bigint;
  startUnrealizedPriceImpact: bigint;
  sumMaxSize: bigint;
  volume: bigint;
  wins: number;
  id: string;
};

export function usePeriodAccountStats(
  chainId: number,
  params: { account?: string; from?: number; to?: number; enabled?: boolean }
) {
  const { account, from, to, enabled = true } = params;

  const { data, error, isLoading } = useSWR<PeriodAccountStats | undefined>(
    enabled && account ? ["usePeriodAccountStats", chainId, account, from, to] : null,
    async () => {
      const [increases, decreases] = await Promise.all([
        fetchTrades({ account: account!.toLowerCase(), action: "increase", from, to, pageSize: 1000 }),
        fetchTrades({ account: account!.toLowerCase(), action: "decrease", from, to, pageSize: 1000 }),
      ]);

      const allTrades = [...increases.items, ...decreases.items];
      if (allTrades.length === 0) return undefined;

      let volume = 0n;
      let realizedPnl = 0n;
      let realizedFees = 0n;
      let wins = 0;
      let losses = 0;

      for (const t of allTrades) {
        volume += bigNumberify(t.size_delta) ?? 0n;
        realizedFees += bigNumberify(t.fee) ?? 0n;
        if (t.realised_pnl) {
          const pnl = bigNumberify(t.realised_pnl) ?? 0n;
          realizedPnl += pnl;
          if (pnl > 0n) wins++;
          else if (pnl < 0n) losses++;
        }
      }

      return {
        closedCount: decreases.items.length,
        cumsumCollateral: 0n,
        cumsumSize: volume,
        losses,
        maxCapital: 0n,
        netCapital: 0n,
        realizedFees,
        realizedPnl,
        realizedPriceImpact: 0n,
        startUnrealizedFees: 0n,
        startUnrealizedPnl: 0n,
        startUnrealizedPriceImpact: 0n,
        sumMaxSize: volume,
        volume,
        wins,
        id: account!,
      };
    }
  );

  return useMemo(() => ({ data, error, loading: isLoading }), [data, error, isLoading]);
}
